import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart3, 
  Package, 
  RefreshCw, 
  Settings, 
  Users, 
  Brain,
  TrendingUp,
  Database,
  Shield,
  LogOut
} from 'lucide-react';
import { useAuth } from '@/components/auth/AuthProvider';
import { useUserMode } from '@/stores/userModeStore';
import { ModeSwitcher } from '@/components/ui/mode-switcher';
import { useNavigate } from 'react-router-dom';

export const AdminDashboard = () => {
  const { profile, signOut, isAdmin } = useAuth();
  const { currentMode } = useUserMode();
  const navigate = useNavigate();

  const adminFeatures = [
    {
      title: 'Analytics Dashboard',
      description: 'View comprehensive business analytics',
      icon: BarChart3,
      action: () => navigate('/analytics'),
      color: 'bg-blue-500',
    },
    {
      title: 'Executive Reporting',
      description: 'Generate executive reports and insights',
      icon: TrendingUp,
      action: () => navigate('/executive-reporting'),
      color: 'bg-green-500',
    },
    {
      title: 'Unified Sync Center',
      description: 'Manage all data synchronization',
      icon: RefreshCw,
      action: () => navigate('/sync-manager'),
      color: 'bg-purple-500',
    },
    {
      title: 'AI Features',
      description: 'Advanced AI-powered tools',
      icon: Brain,
      action: () => navigate('/ai-features'),
      color: 'bg-orange-500',
    },
    {
      title: 'Master Products',
      description: 'Manage product catalog',
      icon: Package,
      action: () => navigate('/master-products'),
      color: 'bg-indigo-500',
    },
    {
      title: 'System Settings',
      description: 'Configure system parameters',
      icon: Settings,
      action: () => navigate('/settings'),
      color: 'bg-red-500',
    },
    {
      title: 'Security Settings',
      description: 'Configure security levels and permissions',
      icon: Shield,
      action: () => navigate('/admin/security-settings'),
      color: 'bg-red-600',
    },
    {
      title: 'User Management',
      description: 'Manage users and permissions',
      icon: Users,
      action: () => navigate('/hr/user-management'),
      color: 'bg-teal-500',
    },
    {
      title: 'Role & Security Management',
      description: 'Manage user roles and system security',
      icon: Shield,
      action: () => navigate('/admin/roles'),
      color: 'bg-purple-600',
    },
    {
      title: 'Data Recovery',
      description: 'Backup and recovery tools',
      icon: Database,
      action: () => navigate('/data-recovery'),
      color: 'bg-gray-500',
    },
  ];

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="text-3xl font-bold">
              Admin Dashboard
            </h1>
            <p className="text-muted-foreground">
              Full system access and management tools
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <ModeSwitcher showLabels={false} />
            <Badge variant="default" className="bg-red-100 text-red-800 border-red-200">
              Admin Role
            </Badge>
            <Button variant="destructive" onClick={signOut}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Orders</p>
                  <p className="text-2xl font-bold">24</p>
                </div>
                <Package className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Staff Online</p>
                  <p className="text-2xl font-bold">8</p>
                </div>
                <Users className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Sync Status</p>
                  <p className="text-2xl font-bold text-green-600">Good</p>
                </div>
                <RefreshCw className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">System Health</p>
                  <p className="text-2xl font-bold text-green-600">100%</p>
                </div>
                <Shield className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Admin Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {adminFeatures.map((feature) => (
            <Card 
              key={feature.title} 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={feature.action}
            >
              <CardHeader className="pb-4">
                <div className={`w-12 h-12 rounded-lg ${feature.color} flex items-center justify-center mb-2`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-lg">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Recent System Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm">System sync completed successfully</span>
                </div>
                <span className="text-xs text-muted-foreground">2 minutes ago</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-sm">New order submitted by staff</span>
                </div>
                <span className="text-xs text-muted-foreground">5 minutes ago</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span className="text-sm">AI processing completed for batch #123</span>
                </div>
                <span className="text-xs text-muted-foreground">10 minutes ago</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};